import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import {
  taxCategoriesGetAll,
  taxCategoriesGetStripeTaxCodes,
  taxCategoriesUpdate,
} from "~/api-refs";
import {
  EMPTY_TAX_CATEGORY_FORM_VALUES,
  type TaxCategoryFormData,
  TaxCategoryFormModal,
  taxCategorySchema,
} from "~/components/settings/tax-category-form-modal";
import type { ComboboxItem } from "~/components/ui/combobox";
import { Dialog, DialogContent } from "~/components/ui/dialog";
import { Spinner } from "~/components/ui/spinner";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

export const Route = createFileRoute(
  "/_authenticated/settings/tax-categories/$categoryId/edit"
)({
  component: EditTaxCategoryModal,
});

function EditTaxCategoryModal() {
  const { categoryId } = Route.useParams();
  const navigate = useNavigate();

  const { data: taxCategories } = useQuery(
    convexQuery(taxCategoriesGetAll, {})
  );
  const taxCategory = taxCategories?.find(
    (category) => category._id === categoryId
  );

  const { data: stripeTaxCodes, isLoading: isLoadingTaxCodes } = useQuery({
    queryKey: ["stripeTaxCodes"],
    queryFn: () => convexClient.action(taxCategoriesGetStripeTaxCodes, {}),
    staleTime: 1000 * 60 * 60,
  });

  const taxCodeOptions: ComboboxItem<string>[] = (stripeTaxCodes ?? []).map(
    (code) => ({
      value: code.id,
      label: code.name,
      keywords: [code.id, code.description.toLowerCase()],
    })
  );

  const form = useForm<TaxCategoryFormData>({
    resolver: zodResolver(taxCategorySchema),
    defaultValues: EMPTY_TAX_CATEGORY_FORM_VALUES,
  });

  useEffect(() => {
    if (!taxCategory) {
      return;
    }

    form.reset({
      name: taxCategory.name,
      taxMethod: taxCategory.taxCode ? "automatic" : "manual",
      value: taxCategory.value ? taxCategory.value.toString() : "",
      taxCode: taxCategory.taxCode ?? "",
    });
  }, [taxCategory, form]);

  const updateTaxCategoryMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: Id<"taxCategories">;
      data: { name: string; value: number; taxCode?: string };
    }) => {
      return convexClient.mutation(taxCategoriesUpdate, { id, data });
    },
    onSuccess: () => {
      ProductEvents.taxCategoryUpdated(categoryId);
    },
  });

  const handleClose = () => {
    void navigate({ to: "/settings/tax-categories" });
  };

  const handleSubmit = async (values: TaxCategoryFormData) => {
    if (updateTaxCategoryMutation.isPending) {
      return;
    }

    try {
      const taxValue =
        values.taxMethod === "automatic"
          ? 0
          : Number.parseFloat(values.value ?? "0");

      const taxCode =
        values.taxMethod === "automatic"
          ? (values.taxCode ?? undefined)
          : undefined;

      await updateTaxCategoryMutation.mutateAsync({
        id: toConvexTableId("taxCategories", categoryId),
        data: {
          name: values.name,
          value: taxValue,
          taxCode,
        },
      });
      toast.success("Tax Category updated successfully");
      handleClose();
    } catch (error) {
      toast.error("Error updating Tax Category");
      logger.exception("Failed to update tax category", error);
    }
  };

  if (!taxCategory) {
    return (
      <Dialog onOpenChange={handleClose} open>
        <DialogContent>
          <div className="flex items-center justify-center py-8">
            <Spinner className="size-6" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <TaxCategoryFormModal
      description="Update the tax category settings."
      form={form}
      isLoadingTaxCodes={isLoadingTaxCodes}
      isSubmitting={updateTaxCategoryMutation.isPending}
      onClose={handleClose}
      onSubmit={handleSubmit}
      submitLabel="Save"
      taxCodeOptions={taxCodeOptions}
      title="Edit Tax Category"
    />
  );
}
